/**
 * WordPress dependencies
 */
import domReady from '@wordpress/dom-ready';
import { registerBlockStyle } from '@wordpress/blocks';
import { registerFormatType, insert, remove } from '@wordpress/rich-text';
import { RichTextToolbarButton } from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import styles from './styles.json';
import './index.css';

/**
 * Soft Hyphen character
 */
const SOFT_HYPHEN = '\u00AD';

/**
 * Check if there's a soft hyphen at the current cursor position
 *
 * @param {Object} value - Rich text value object
 * @return {boolean} Whether cursor is at a soft hyphen
 */
const hasSoftHyphenAtCursor = (value) => {
	const { text, start, end } = value;

	// Check if cursor is right after a soft hyphen
	if (start > 0 && text[start - 1] === SOFT_HYPHEN) {
		return true;
	}

	// Check if cursor is right before a soft hyphen
	if (end < text.length && text[end] === SOFT_HYPHEN) {
		return true;
	}

	// Check if there's a soft hyphen in the selection
	if (start !== end) {
		const selectedText = text.slice(start, end);
		return selectedText.includes(SOFT_HYPHEN);
	}

	return false;
};

/**
 * Soft Hyphen Button Component
 *
 * @param {Object}   props          - Component props
 * @param {boolean}  props.isActive - Whether the button is active
 * @param {Object}   props.value    - Rich text value
 * @param {Function} props.onChange - Change handler
 * @return {JSX.Element} The toolbar button
 */
const SoftHyphenButton = ({ isActive, value, onChange }) => {
	const handleClick = () => {
		const { text, start, end } = value;

		if (isActive) {
			// Remove soft hyphen
			let newStart = start;
			let newEnd = end;

			// If cursor is right after a soft hyphen, select it
			if (start === end && start > 0 && text[start - 1] === SOFT_HYPHEN) {
				newStart = start - 1;
				newEnd = start;
			}
			// If cursor is right before a soft hyphen, select it
			else if (
				start === end &&
				end < text.length &&
				text[end] === SOFT_HYPHEN
			) {
				newStart = end;
				newEnd = end + 1;
			}
			// If there's a selection with soft hyphens, remove them from selection
			else if (start !== end) {
				const selectedText = text.slice(start, end);
				const cleanedText = selectedText.replace(
					new RegExp(SOFT_HYPHEN, 'g'),
					''
				);

				onChange(
					insert(remove(value, start, end), cleanedText, start, start)
				);
				return;
			}

			// Remove the single soft hyphen
			onChange(remove(value, newStart, newEnd));
		} else {
			// Insert soft hyphen at cursor position
			onChange(insert(value, SOFT_HYPHEN, start, end));
		}
	};

	return (
		<RichTextToolbarButton
			icon="editor-code"
			title={__('Soft Hyphen', 'flavor-starter-theme')}
			onClick={handleClick}
			isActive={isActive}
		/>
	);
};

/**
 * Register the soft hyphen format type
 */
registerFormatType('theme/soft-hyphen', {
	title: __('Soft Hyphen', 'flavor-starter-theme'),
	tagName: 'span',
	className: null,
	edit: ({ isActive, value, onChange }) => {
		// Calculate isActive based on cursor position
		const active = hasSoftHyphenAtCursor(value);

		return (
			<SoftHyphenButton
				isActive={active}
				value={value}
				onChange={onChange}
			/>
		);
	},
});

/**
 * Run on DOM ready
 */
domReady(function () {
	/**
	 * Register custom button styles
	 */
	styles.forEach((style) => {
		registerBlockStyle('core/heading', style);
	});
});
